import {
  Box,
  Button, CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { FilterRow, joins, operators } from "../../molecules/FilterRow";
import add from "../../../../public/assets/icons/add.svg";
import theme from "../../../theme/Theme";
import { directoryById } from "../../../queries/DirectoryData";
import {
  buildColumnsDict,
  buildSearchFilter,
  caseContext, defaultColumns,
  getColId,
  recursiveBuildSchema,
} from "../workItemsTable";
import { CreateProfile, UpdateProfile } from "../../../queries/profileData";
import { getGroups } from "../../../queries/groups";
import { Coders } from "../../../queries/Workers";
import ChevronDown from "../../../../public/assets/icons/chevron-down.svg";
import {
  inverseServiceLineMapper,
  policiesContext,
  serviceLineMapper
} from "../../../utils/common";
import {Filters} from "../../molecules/Filters";

interface GroupOption {
  label: string;
  value: string;
}

type ProfileVariables = {
  profile: any,
  updateProfileId?: String
}

const CODING_MANAGER = "coding_manager";

export const ProfileForm = (props: any) => {
  const { client, showForm, existingProfile, directoryType, enableQueueViews } = props;
  const [uiFilters, setUiFilters] = React.useState(
    existingProfile.filters ?
    existingProfile.filters.map((f, i) => i)
    : []);
  const [clearFirstFilter, setClearFirstFilter] = React.useState(false);
  const [filters, setFilters] = React.useState<any>(existingProfile?.filters ?? []);
  const [description, setDescription] = React.useState(existingProfile?.description ?? "");
  
  const [optionsDict, setOptionsDict] = React.useState({});
  const [columnDefs, setColumnDefs] = React.useState<any>([]);
  const [detailGridOptions, setDetailGridOptions] = React.useState({});
  
  const [loading, setLoading] = React.useState(true);
  
  const [groupsData, setGroupsData] = React.useState<GroupOption[]>([]);
  const [allCoders, setAllCoders] = React.useState([]);
  const [formValues, setFormValues] = React.useState({
    name:{
      value: existingProfile?.name ?? "",
      error:false,
      errorMessage:'You must enter a name‼️'
    },
    selectedGroup:{
      value:existingProfile?.group_id ?? "",
      error:false,
      errorMessage:'You must select a group‼️'
    },
    service:{
      value:existingProfile?.service ?? "",
      error:false,
      errorMessage:'You must select a service‼️'
    }
  })

  const { roles, userContext } = React.useContext(policiesContext);

  const isDetail = Object.values(existingProfile).length !== 0;
  const nonManagerDetail = isDetail && !roles.includes(CODING_MANAGER);

  //region Functions
  const getGroupsData = async () => {
    await props?.client
      .query({
        query: getGroups,
        fetchPolicy: "no-cache",
      })
      .then((response: any) => {
        const groups = [];
        response?.data?.Groups?.data?.forEach((group) => {
          groups.push({
            label: group?.data?.name,
            value: group?.data?.group_id,
          });
        });
        setGroupsData(groups);
      })
      .catch((error: any) => {
        console.error("error fetching groups", error);
      });
  };

  const getWorkItemMetadata = async () => {
    const variables = { DirectoryType: "workitem" };
    await props?.client
      .query({
        query: directoryById,
        variables: variables,
        fetchPolicy: "no-cache",
      })
      .then((response: any) => {
        const relationshipDict = {};
        response?.data?.DirectoryByType?.schema?.relationships?.map(
          (relationship) => {
            if (relationshipDict[relationship?.srcId]) {
              relationshipDict[relationship?.srcId]?.push({
                childId: relationship?.destnId,
                mappingName: relationship?.name,
              });
            } else {
              relationshipDict[relationship?.srcId] = [
                {
                  childId: relationship?.destnId,
                  mappingName: relationship?.name,
                },
              ];
            }
          }
        );
        const modelDict = {};
        var rootModel;
        response?.data?.DirectoryByType?.schema?.models?.map((model) => {
          modelDict[model?.id] = model;
          if (model?.root) {
            rootModel = model;
          }
        });
        const cellParams = recursiveBuildSchema(
          rootModel,
          relationshipDict,
          modelDict
        );
        const sortedColumnDefs = [
          ...cellParams?.detailGridOptions?.columnDefs,
          ...response?.data?.DirectoryByType?.factKeys,
        ].sort((a, b) => {
          let fa = a.headerName?.toLowerCase(),
            fb = b.headerName?.toLowerCase();

          if (fa < fb) {
            return -1;
          }
          if (fa > fb) {
            return 1;
          }
          return 0;
        });
        setDetailGridOptions({
          ...cellParams?.detailGridOptions,
          columnDefs: sortedColumnDefs
        });
        setColumnDefs(cellParams?.detailGridOptions?.columnDefs);
        // setDetailCellRendererParams(
        //   cellParams?.detailGridOptions?.detailCellRendererParams
        // );
        const columnNames = buildColumnsDict(cellParams?.detailGridOptions);
        const dict = {};
        columnNames?.map((columnName) => {
          dict[columnName] = [];
        });
        setOptionsDict(dict);
        const updatedFilters = filters?.map(filter => {
          const column = sortedColumnDefs?.find(col => col.field === filter.fieldName);
          return {
            ...filter,
            fieldId: getColId(column)
          };
        });

        setFilters(updatedFilters);
      })
      .catch((error: any) => {
        console.error(
          "error fetching directory metadata for profile form",
          error
        );
      });
  };

  const formValidationForRequireFields = ()=>{
    return Object.keys(formValues).some((el)=> {
      if(formValues[el].error || formValues[el].value.trim().length ==0){
        setFormValues({...formValues,[el]:{
          ...formValues[el],error:true
        }})
        return true;
      }
      return false;
      });
  }
  
  const createProfile = async (e) => {
    // @ts-ignore
    window.DD_RUM && window.DD_RUM.startView("Open Worklist Create");
    e.preventDefault();
    const filtersJson = buildSearchFilter(filters);

    if(!formValidationForRequireFields()){
      let variables: ProfileVariables = {
        profile: {
          filters: isDetail ? [{ rules: filtersJson }] : { rules: filtersJson },
          name: formValues.name.value,
          type: "LOCAL_COLLECTION",
          description,
          group_id: formValues.selectedGroup.value,
          service: serviceLineMapper[ formValues.service.value] ??  formValues.service.value,
          ...(enableQueueViews && { columns: (detailGridOptions as any)?.columnDefs }),
        },
      };

      if (isDetail) {
        variables = {
          ...variables,
          updateProfileId: existingProfile.collection_id
        }
      }
      await props?.client
        ?.mutate({
          mutation: isDetail ? UpdateProfile : CreateProfile,
          variables: variables,
          fetchPolicy: "no-cache",
        })
        .then((response: any) => {
          showForm({}, false);
        })
        .catch((err) => {
          console.error(err);
        });
    }
  };

  const handleChangeForRequireField = (e) => {
    const {name, value} = e.target;
    setFormValues({
      ...formValues,
      [name]:{
        ...formValues[name],
        value:value,
        error: value.length === ""
      }
    })
  }

  const getCoders = async () => {
    const variables = { pageNumber: 0, pageSize: 100 };
    await props?.client
      .query({
        query: Coders,
        variables: variables,
        fetchPolicy: "no-cache",
      })
      .then((response: any) => {
        const coders = response.data.Coders.data.sort((a, b) => a?.data?.last_name.localeCompare(b?.data?.last_name))
        setAllCoders(coders);
      })
      .catch((error: any) => {
        console.log(error);
    });
  }
  //endregion

  //region Lifecycle
  useEffect(() => {
    const fetchData = async () => {
        setLoading(true);
        const promises = [];
        promises.push(getGroupsData());
        promises.push(getWorkItemMetadata());
        promises.push(getCoders());
        await Promise.all(promises);
        setLoading(false);
    };
    
    fetchData();
  }, []);
  //endregion
  
  return (
    <Box>
      <form noValidate onSubmit={createProfile} >
        <Box>
          <Grid container marginBottom={"20px"}>
            <Grid item xs={6} alignSelf="center">
              <Typography variant="h2">{isDetail ? "Queue Details" : "Add Queue"}</Typography>
            </Grid>
            <Grid
                item
                container
                xs={6}
                alignItems="flex-end"
                justifyContent={"flex-end"}
                gap="20px"
            >
              {!nonManagerDetail ? (
                      <>
                        <Grid item>
                          <Button variant="outlined" onClick={() => showForm({}, false)}>Cancel</Button>
                        </Grid>
                        <Grid item>
                          <Button  type="submit" variant="contained">
                            {!isDetail ? "Create" : "Update"}
                          </Button>
                        </Grid>
                      </>
                  ) :
                  null}
            </Grid>
          </Grid>
        </Box>
        <Divider sx={{ marginTop: "20px", marginBottom: "20px" }} />

        <Box sx={{ height: "100%", display: "flex" }}>
          <Box sx={{ width: "100%" }}>
            <Box display="flex" flexDirection="column" width="50%">
              <InputLabel
                  required
                  htmlFor="profile-name-input"
                  sx={{ display: "flex" }}
              >
                <Typography variant="caption2">Profile name</Typography>
              </InputLabel>

              <TextField
                  required
                  variant="outlined"
                  id="profile-name-input"
                  sx={{
                    marginBottom: "20px",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        border: `1px solid ${theme.palette.artisticStroke200.main}`,
                      },
                      "&:hover fieldset": {
                        border: `1px solid ${theme.palette.artisticGrey400.main}`,
                      },
                      "&.Mui-focused fieldset": {
                        border: `1px solid ${theme.palette.artisticGrey400.main}`,
                      },
                    },
                  }}
                  placeholder="Enter profile name"
                  onChange={(event) => {
                    handleChangeForRequireField(event);
                    // setName(event.target.value);
                  }}
                  value={formValues.name.value}
                  name="name"
                  disabled={nonManagerDetail}
                  error={formValues.name.error}
                  helperText={formValues.name.error && formValues.name.errorMessage}
              />
            </Box>

            <Box display="flex" flexDirection="column" width="50%">
              <InputLabel htmlFor="profile-description-input">
                <Typography variant="caption2">Description</Typography>
              </InputLabel>
              <TextField
                  variant="outlined"
                  id="profile-description-input"
                  sx={{
                    marginBottom: "20px",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        border: `1px solid ${theme.palette.artisticStroke200.main}`,
                      },
                      "&:hover fieldset": {
                        border: `1px solid ${theme.palette.artisticGrey400.main}`,
                      },
                      "&.Mui-focused fieldset": {
                        border: `1px solid ${theme.palette.artisticGrey400.main}`,
                      },
                    },
                  }}
                  placeholder="Enter a description about the profile"
                  minRows={3}
                  multiline
                  onChange={(event) => {
                    setDescription(event.target.value);
                  }}
                  value={description}
                  disabled={nonManagerDetail}
              />
            </Box>

            <InputLabel htmlFor="profile-filters">
              <Typography variant="body1" sx={{ marginBottom: "10px" }}>
                Add and customize rules for the profile.
              </Typography>
            </InputLabel>

            {/*Filters*/}
            {loading ? (
                  <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", width: "250px", margin: "20px"}}>
                    <CircularProgress color="primary" size={20} />
                  </Box>
                ) : (
                  <Filters
                      client={client}
  
                      directoryType={"workitem"}
                      detailGridOptions={detailGridOptions}
  
                      // @ts-ignore
                      columnOptions={detailGridOptions?.columnDefs}
                      optionsDict={optionsDict}
                      setOptionsDict={setOptionsDict}
  
                      filters={filters}
                      setFilters={setFilters}
  
                      allCoders={allCoders}
                      // allGroups={allGroups}
  
                      hasBorder={true}
                      containerStyles={{ padding: "10px", marginBottom: "10px" }}
  
                      isEditable={!nonManagerDetail}
                  />
            )}

            <Box display="flex" flexDirection="column" width="50%">
              <InputLabel
                  required
                  htmlFor="group_id_select"
                  sx={{ display: "flex" }}
              >
                <Typography variant="caption2">Group</Typography>
              </InputLabel>
              <FormControl sx={{ width: "100%", marginBottom: "2em"}}>
                <Select
                    id={`group_id_select`}
                    value={formValues.selectedGroup.value}
                    name="selectedGroup"
                    onChange={nonManagerDetail ? () => {} : (event) => {handleChangeForRequireField(event);}}
                    IconComponent={() => (
                        <IconButton disableRipple>
                          <img src={ChevronDown} alt="chevron-down" />
                        </IconButton>
                    )}
                    sx={{
                      height: "37px",
                      width: "50%",
                      paddingRight: "8px",
                      borderRadius: 0,
                      "& .MuiSelect-select": {
                        paddingRight: "16px !important",
                      },
                      "& .MuiSelect-icon": {
                        color: theme.palette.artisticTextInteractive.main,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: `1px solid ${formValues.selectedGroup.error ?  theme.palette.classicAlert500.main :theme.palette.artisticStroke200.main} !important`,
                      },
                      "&.Mui-focused fieldset": {
                        border: `1px solid ${theme.palette.artisticGrey400.main} !important`,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: `${formValues.selectedGroup.error ?  theme.palette.classicAlert500.main :theme.palette.artisticGrey400.main} !important`,
                      },
                    }}
                    disabled={nonManagerDetail}
                    required
                >
                  {groupsData?.map((group) => (
                      <MenuItem
                          key={group?.value}
                          value={group?.value}
                          sx={{ padding: "8px 16px" }}
                      >
                        {group?.label}
                      </MenuItem>
                  ))}
                </Select>
                {formValues.selectedGroup.error && <FormHelperText error={true}>{formValues.selectedGroup.errorMessage}</FormHelperText>}
              </FormControl>
            </Box>
            <Box display="flex" flexDirection="column" width="50%">
              <InputLabel
                  required
                  htmlFor="group_id_select"
                  sx={{ display: "flex" }}
              >
                <Typography variant="caption2">Service Line</Typography>
              </InputLabel>
              <FormControl sx={{ width: "100%" , marginBottom: "2em"}}>
                <Select
                    id="service_line_id_select"
                    value={inverseServiceLineMapper[formValues.service.value] ?? formValues.service.value}
                    name="service"
                    onChange={nonManagerDetail ? () => {} :  (event) => {handleChangeForRequireField(event);}}
                    IconComponent={() => (
                        <IconButton disableRipple>
                          <img src={ChevronDown} alt="chevron-down" />
                        </IconButton>
                    )}
                    sx={{
                      height: "37px",
                      width: "50%",
                      paddingRight: "8px",
                      borderRadius: 0,
                      "& .MuiSelect-select": {
                        paddingRight: "16px !important",
                      },
                      "& .MuiSelect-icon": {
                        color: theme.palette.artisticTextInteractive.main,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: `1px solid ${formValues.service.error ?  theme.palette.classicAlert500.main :theme.palette.artisticStroke200.main} !important`,
                      },
                      "&.Mui-focused fieldset": {
                        border: `1px solid ${theme.palette.artisticGrey400.main} !important`,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: `${formValues.selectedGroup.error ?  theme.palette.classicAlert500.main :theme.palette.artisticGrey400.main} !important`,
                      },
                    }}
                    disabled={nonManagerDetail}
                    required
                >
                  {userContext.activeContext.serviceLines?.map((serviceLine) => (
                      <MenuItem
                          key={serviceLine?.id}
                          value={serviceLine?.name}
                          sx={{ padding: "8px 16px" }}
                      >
                        {serviceLine?.displayName || serviceLine?.name}
                      </MenuItem>
                  ))}
                </Select>
                {formValues.service.error && <FormHelperText error={true}>{formValues.service.errorMessage}</FormHelperText>}
              </FormControl>
            </Box>
          </Box>
        </Box>
      </form>
    </Box>
  );
};
